import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Building, 
  Briefcase, 
  Phone, 
  Mail, 
  ArrowLeft,
  Save,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { interviewService, InterviewFormData } from '../../services/interviewService';
import { useUserSearch } from '../../hooks/useUserSearch';
import { useUserApplications } from '../../hooks/useUserApplications';
import AutoCompleteInput from '../ui/AutoCompleteInput';
import CompanyDropdown from '../ui/CompanyDropdown';

const AddInterviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { users, loading: searchLoading, error: searchError, searchUsers, clearUsers } = useUserSearch();
  const { companies, loading: companiesLoading, error: companiesError, loadUserApplications } = useUserApplications();

  const [formData, setFormData] = useState<InterviewFormData>({
    userId: '',
    userName: '',
    userEmail: '',
    companyName: '',
    position: '',
    interviewDate: new Date(),
    interviewTime: '',
    interviewLocation: '',
    interviewType: 'in-person',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    contactRole: '',
    notes: ''
  });

  // Debounce per la ricerca utenti
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.userName.length >= 2) {
        searchUsers(formData.userName);
      } else {
        clearUsers();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.userName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      interviewDate: new Date(e.target.value)
    }));
  };

  const handleUserSelect = (user: { uid: string; name: string; email: string }) => {
    setFormData(prev => ({
      ...prev,
      userId: user.uid,
      userName: user.name,
      userEmail: user.email
    }));
    clearUsers();
    
    // Carica le candidature dell'utente per popolare le aziende
    loadUserApplications(user.uid);
  };

  const handleCompanyChange = (company: string) => {
    setFormData(prev => ({
      ...prev,
      companyName: company
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!userProfile?.uid) {
        throw new Error('Utente non autenticato');
      }

      await interviewService.createInterview(formData, userProfile.uid);
      navigate('/moderator/interviews');
    } catch (error) {
      console.error('Error creating interview:', error);
      setError('Errore nella creazione dell\'intervista');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/moderator/interviews')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Nuova Intervista</h1>
              <p className="text-sm sm:text-base text-gray-600">Aggiungi una nuova intervista per un utente</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Informazioni Candidato */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Informazioni Candidato
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AutoCompleteInput
                value={formData.userName}
                onChange={(value) => setFormData(prev => ({ ...prev, userName: value }))}
                onUserSelect={handleUserSelect}
                placeholder="Cerca per nome o email..."
                label="Nome Candidato"
                required
                users={users}
                loading={searchLoading}
                error={searchError}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Candidato *
                </label>
                <input
                  type="email"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@esempio.com"
                />
              </div>
            </div>
          </div>

          {/* Informazioni Azienda */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Informazioni Azienda
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <CompanyDropdown
                value={formData.companyName}
                onChange={handleCompanyChange}
                companies={companies}
                loading={companiesLoading}
                placeholder="Nome azienda"
                label="Nome Azienda"
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posizione *
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ruolo/posizione"
                />
              </div>
            </div>
          </div>

          {/* Dettagli Intervista */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Dettagli Intervista
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Intervista *
                </label>
                <input
                  type="date"
                  value={formData.interviewDate.toISOString().split('T')[0]}
                  onChange={handleDateChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ora Intervista *
                </label>
                <input
                  type="time"
                  name="interviewTime"
                  value={formData.interviewTime}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Intervista
                </label>
                <select
                  name="interviewType"
                  value={formData.interviewType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="in-person">In presenza</option>
                  <option value="video">Video call</option>
                  <option value="phone">Telefonica</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Luogo Intervista
              </label>
              <input
                type="text"
                name="interviewLocation"
                value={formData.interviewLocation}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Indirizzo o link video call"
              />
            </div>
          </div>

          {/* Contatto Azienda */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Contatto Azienda
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Persona di Riferimento
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome e cognome"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ruolo
                </label>
                <input
                  type="text"
                  name="contactRole"
                  value={formData.contactRole}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Es. HR Manager, Responsabile"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Contatto
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@azienda.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefono Contatto
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+41 79 000 12 12"
                />
              </div>
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Aggiuntive
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Note, dettagli aggiuntivi, preparazione richiesta..."
            />
          </div>

          {/* Pulsanti */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/moderator/interviews')}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center"
            >
              <X className="w-4 h-4 mr-2" />
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : 'Salva Intervista'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInterviewPage; 